/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from "react";
import copyIcon from "dndimages/copy-icon.png";
import { Approval, DesiredVisibility, ViolationSummary, hasOutstandingViolations, isAwaitingApproval } from "model/Visibility";

type Intent = 'Private' | 'Shared' | 'Public';

interface ShareMonsterModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;                       // The public link to this monster.
  desiredVisibility: DesiredVisibility;
  approval: Approval | null;
  violationSummary: ViolationSummary;
  onSelectVisibility: (intent: Intent) => void;
  // Open the owner's reports view (offered when there are outstanding violations to resolve).
  onViewReports: () => void;
  // A rejection message from the last share attempt (e.g. the monster contains profanity), if any.
  error?: string;
  // View-only mode (another user's monster, or a catalog monster): just show the link + copy button,
  // no Private/Shared/Public chooser, pending message, or content-guidelines note.
  viewOnly?: boolean;
  viewLabel?: string; // caption shown above the link in view-only mode
}

// The Share modal. Lets the owner choose Private / Shared / Public (desiredVisibility). Each choice
// is applied and saved immediately (no separate Update step). Shared and Public are requests; the
// link works immediately, but Public community listing needs approval, and any outstanding report
// hides the monster. Once not Private the monster is read-only.
const ShareMonsterModal: React.FC<ShareMonsterModalProps> = ({ isOpen, onClose, url, desiredVisibility, approval, violationSummary, onSelectVisibility, onViewReports, error, viewOnly, viewLabel }) => {
  const [copied, setCopied] = useState<boolean>(false);

  // The modal stays mounted (returns null) when closed; reset the transient button states each
  // time it reopens, the link changes, or the visibility changes.
  useEffect(() => {
    setCopied(false);
  }, [isOpen, url, desiredVisibility]);

  if (!isOpen) return null;

  const intent: Intent = desiredVisibility;
  const flagged = hasOutstandingViolations(violationSummary);
  const isPending = isAwaitingApproval(desiredVisibility, approval, violationSummary);

  const copyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => setCopied(true))
        .catch(() => { /* clipboard unavailable; the link is still shown for manual copy */ });
    }
  };

  const optionRow = (value: Intent, title: string, body: React.ReactNode, disabled = false) => (
    <label style={{
      display: 'block', padding: '10px', border: '1px solid #888', borderRadius: '6px',
      margin: '6px 0', background: intent === value ? 'rgba(167,211,253,0.6)' : 'rgba(255,255,255,0.5)',
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1
    }}>
      <input type="radio" name="visibilityIntent" checked={intent === value} disabled={disabled}
             onChange={() => onSelectVisibility(value)} style={{ marginRight: '8px' }} />
      <b>{title}</b>
      <div style={{ fontSize: 'small', marginTop: '4px', marginLeft: '22px' }}>{body}</div>
    </label>
  );

  const linkRow = (
    <div style={{ fontSize: 'small' }}>
      Link:
      <span style={{
        flex: 1, wordBreak: 'break-all', fontWeight: 'bold', background: 'rgba(255,255,255,0.5)',
        border: '1px solid #888', borderRadius: '4px', padding: '8px', fontSize: 'x-small', marginLeft: '20px'
      }}>
        <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
           style={{ wordBreak: 'break-all', fontWeight: 'bold' }}>{url}</a>{' '}
      </span>
      <button type="button" title="Copy Link"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); copyLink(); }}
              style={{ lineHeight: 1, cursor: 'pointer', verticalAlign: 'middle' }}>
        {copied ? '✓' : <img src={copyIcon} alt="Copy Link" />}
      </button>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ width: '550px', height: 'auto', minHeight: viewOnly ? '160px' : '350px' }} onClick={(e) => e.stopPropagation()}>
        <span className="modal-title" style={{ display: 'block', textAlign: 'center' }}>{viewOnly ? 'Monster Link' : 'Share this Monster'}</span>

        {viewOnly ? (
          // View-only: another user's or a catalog monster — just show the shareable link.
          <div>
            {viewLabel && <p style={{ fontSize: 'small' }}>{viewLabel}</p>}
            {linkRow}
          </div>
        ) : (
          <>
            <div>
              {optionRow('Private', 'Private', 'No one else can see this monster.')}
              {optionRow('Shared', 'Shared',
                <>Anyone with the link below can see this monster. You can not edit your monster in this state.</>,
                flagged)}
              {optionRow('Public', 'Public', 'As above, but other Monster Forge users can also find your monster in the GGG community and in the monster search feature (once approved).',
                flagged)}
            </div>
            {error && (
              <p style={{ fontSize: 'small', fontWeight: 'bold', color: '#c00' }}>{error}</p>
            )}
            {flagged && (
              <p style={{ fontSize: 'small', fontWeight: 'bold', color: '#c00' }}>
                This monster has outstanding violations and is hidden from others. You can't share it
                again until you resolve each one.{' '}
                <button type="button" onClick={(e) => { e.stopPropagation(); onViewReports(); }}
                        style={{ font: 'inherit', color: '#c00', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                  View reports
                </button>
              </p>
            )}
            {!flagged && isPending && (
              <p style={{ fontSize: 'small', fontWeight: 'bold', color: '#8a5a00' }}>
                This monster's visibility is pending a review process.
              </p>
            )}
            {intent !== 'Private' && (
              <>
                {linkRow}
                <p style={{ fontSize: 'small' }}>
                  <b>NOTE:</b> By making this monster viewable by others (Shared or Public), you confirm that its content follows our{' '}
                  <a href="/monsterforge/terms" target="_blank" rel="noreferrer">Content Guidelines</a> and that you
                  are solely responsible for it. Content that violates the guidelines will be removed and may result in
                  the suspension of your account. Multiple or egregious violations will result in your account being
                  permanently banned without refunds, compensation or recourse.
                </p>
              </>
            )}
          </>
        )}

        <div className="modal-actions" style={{ textAlign: 'center' }}>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ShareMonsterModal;
